export function saveRequirementsSwalOptions() {
  return {
    title: '¿Cómo guardar los requisitos?',
    html: `
      <p class="text-start small mb-2">Se guardará el checklist del proceso.</p>
      <ul class="text-start small mb-0">
        <li><b>Obligatorios activos</b>: se precargan en la solicitud y hay que subirlos.</li>
        <li><b>Activos no obligatorios</b>: van al snapshot para complementar con un select (no se precargan solos).</li>
        <li><b>Solo nuevas</b>: las solicitudes abiertas mantienen su snapshot actual.</li>
        <li><b>Aplicar a abiertas</b>: actualiza snapshots, crea slots de obligatorios faltantes y quita los que ya no estén activos.</li>
      </ul>
    `,
    icon: 'question' as const,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Solo nuevas',
    denyButtonText: 'Aplicar a abiertas',
    cancelButtonText: 'Cancelar'
  };
}

/** Mismo alcance Solo nuevas / Aplicar a abiertas al cambiar vencimiento (u otros) del tipo. */
export function saveDocumentTypeSwalOptions() {
  return {
    title: '¿Cómo guardar el tipo de documento?',
    html: `
      <p class="text-start small mb-2">El catálogo del tipo (p. ej. fecha de vencimiento) se actualizará siempre.</p>
      <ul class="text-start small mb-0">
        <li><b>Solo nuevas</b>: las solicitudes abiertas mantienen su snapshot; no se notifican pantallas abiertas.</li>
        <li><b>Aplicar a abiertas</b>: sincroniza snapshots de procesos que usan este tipo y refresca solicitudes abiertas.</li>
      </ul>
    `,
    icon: 'question' as const,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Solo nuevas',
    denyButtonText: 'Aplicar a abiertas',
    cancelButtonText: 'Cancelar'
  };
}

export function saveDocumentTypeSuccessText(applyToOpen: boolean, affectedCount: number): string {
  if (applyToOpen) {
    return `Tipo actualizado. Snapshots aplicados a ${affectedCount} solicitud(es) abierta(s).`;
  }
  return 'Tipo actualizado. Las solicitudes abiertas no se modificaron.';
}

export function saveRequirementsSuccessText(applyToOpen: boolean, affectedCount: number): string {
  if (applyToOpen) {
    return `Catálogo actualizado. Requisitos aplicados a ${affectedCount} solicitud(es) abierta(s).`;
  }
  return 'Catálogo actualizado. Las solicitudes abiertas no se modificaron.';
}

/** Formatea fecha de auditoría para UI. */
export function formatAuditWhen(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Etiqueta de rango paginado (ej. 1–50 de 120). */
export function auditRangeLabel(total: number, offset: number, pageSize: number): string {
  if (!total) return '0';
  const from = offset + 1;
  const to = Math.min(offset + pageSize, total);
  return `${from}–${to} de ${total}`;
}

function badgeByAction(action: string | undefined): string | null {
  if (action === 'ROLLBACK') return 'text-secondary';
  if (action === 'APPLY_OPEN') return 'text-warning';
  if (action === 'SAVE_CATALOG') return 'text-primary';
  return null;
}

export function actionBadgeClass(row: any): string {
  const byAction = badgeByAction(row?.action);
  if (byAction) return byAction;
  if ((row?.itemsBefore ?? 0) === 0 && (row?.itemsAfter ?? 0) > 0) return 'text-success';
  return 'text-primary';
}

export function auditActorLabel(row: any): string {
  if (row?.userName) return row.userName;
  if (row?.roleName) return row.roleName;
  return '—';
}

export function itemsChangeLabel(row: any): string {
  const before = row?.itemsBefore ?? row?.stepsBefore ?? '—';
  const after = row?.itemsAfter ?? row?.stepsAfter ?? '—';
  return `${before} → ${after}`;
}

export function rollbackSuccessText(applyToOpen: boolean, affectedCount: number): string {
  if (applyToOpen) {
    return `Catálogo restaurado. Requisitos aplicados a ${affectedCount} solicitud(es) abierta(s).`;
  }
  return 'El catálogo de documentos del proceso quedó en esa versión.';
}

export function rollbackConfirmHtml(processLabel: string, whenLabel: string): string {
  return `
    <p class="text-start small mb-2">
      Se reemplazará el checklist actual de
      <b>${processLabel}</b>
      por la versión del
      <b>${whenLabel}</b>.
    </p>
    <ul class="text-start small mb-0">
      <li><b>Solo catálogo</b>: las solicitudes abiertas mantienen su snapshot.</li>
      <li><b>Aplicar a abiertas</b>: también reescribe el snapshot y crea slots faltantes.</li>
    </ul>
  `;
}

export function rollbackSwalOptions(html: string) {
  return {
    title: '¿Restaurar esta versión?',
    html,
    icon: 'warning' as const,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Solo catálogo',
    denyButtonText: 'Aplicar a abiertas',
    cancelButtonText: 'Cancelar'
  };
}

export function rollbackSuccessSwalOptions(applyToOpen: boolean, affectedCount: number) {
  return {
    icon: 'success' as const,
    title: 'Versión restaurada',
    text: rollbackSuccessText(applyToOpen, affectedCount),
    timer: 2800,
    showConfirmButton: false
  };
}
